import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLaunchDarkly } from "integrations/launch-darkly/LaunchDarklyContext";
import { FeatureFlags } from "integrations/launch-darkly/utils";
import { logError } from "shared/services/ErrorReporting";
import { GlobalLoading } from "components/loading";
import { useAuth } from "./AuthContext";
import { toast } from "@/utils/toast";
import { useTranslation } from "react-i18next";
import { useChanged } from "@/hooks/useChanged";

const clientSideID = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID;
const isTesting = import.meta.env.TEST;
if (!clientSideID && !isTesting) {
  logError(
    "VITE_LAUNCH_DARKLY_CLIENT_ID is not set, feature flags will not work",
  );
}

interface IFeatureFlagContext {
  features: FeatureFlags;
}

export const FeatureFlagContext = createContext<IFeatureFlagContext>(
  undefined as never,
);

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { authUser } = useAuth();
  const { features, setContext, clearContext, identified, error } =
    useLaunchDarkly();
  const { t } = useTranslation();

  useEffect(() => {
    if (!authUser) {
      clearContext();
      return;
    }
    setContext("user", {
      key: authUser.uid,
      email: authUser.email ?? undefined,
      name: authUser.displayName ?? undefined,
    });
  }, [authUser, setContext, clearContext]);

  useChanged(() => {
    if (authUser && error) {
      toast.error(t("lauchDarklyUnavailable"));
    }
  }, [error, t, authUser]);

  const flagsReady = !authUser || identified;

  const value = useMemo(() => ({ features }), [features]);

  if (!flagsReady && !error) {
    return <GlobalLoading />;
  }

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const ctxt = useContext(FeatureFlagContext);
  if (!ctxt) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  return ctxt;
};
